import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Plus, BookOpen, Clock, Trash2, Play, Edit2 } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [sets, setSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteId, setDeleteId] = useState(null);

    useEffect(() => {
        fetchSets();
    }, []);

    const fetchSets = async () => {
        try {
            const response = await axios.get(`${API}/sets`);
            setSets(response.data);
        } catch (error) {
            console.error('Failed to fetch sets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await axios.delete(`${API}/sets/${deleteId}`);
            setSets(sets.filter(s => s.id !== deleteId));
        } catch (error) {
            console.error('Failed to delete set:', error);
        } finally {
            setDeleteId(null);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <main className="pt-24 pb-16 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Greeting */}
                    <div className="mb-12">
                        <h1 
                            data-testid="dashboard-greeting"
                            className="font-serif text-4xl md:text-5xl tracking-tight mb-2"
                        >
                            Welcome back, {user?.name?.split(' ')[0]}
                        </h1>
                        <p className="text-muted-foreground font-sans text-lg">
                            {sets.length > 0 
                                ? `You have ${sets.length} flashcard set${sets.length === 1 ? '' : 's'}`
                                : 'Start by creating your first flashcard set'
                            }
                        </p>
                    </div>

                    {/* Main Content */}
                    <div>
                            <div className="flex items-center justify-between mb-8">
                                <h2 className="font-mono text-xs tracking-widest uppercase text-muted-foreground">
                                    Your Sets
                                </h2>
                                <Button
                                    onClick={() => navigate('/create')}
                                    data-testid="create-set-button"
                                    className="rounded-none btn-lift"
                                >
                                    <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                    New Set
                                </Button>
                            </div>

                            {loading ? (
                                <div className="sets-grid">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-48 bg-muted/50 animate-pulse" />
                                    ))}
                                </div>
                            ) : sets.length === 0 ? (
                                <div 
                                    data-testid="empty-state"
                                    className="border border-dashed border-border py-16 flex flex-col items-center justify-center"
                                >
                                    <BookOpen className="w-12 h-12 text-muted-foreground mb-4" strokeWidth={1} />
                                    <h3 className="font-serif text-xl mb-2">No flashcard sets yet</h3>
                                    <p className="text-muted-foreground text-sm mb-6">
                                        Create your first set to start learning
                                    </p>
                                    <Button
                                        onClick={() => navigate('/create')}
                                        data-testid="empty-create-button"
                                        className="rounded-none"
                                    >
                                        <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Create Set
                                    </Button>
                                </div>
                            ) : (
                                <div className="sets-grid">
                                    {sets.map(set => (
                                        <div
                                            key={set.id}
                                            data-testid={`set-card-${set.id}`}
                                            className="group bg-card border border-border card-shadow-light p-6 flex flex-col"
                                        >
                                            <div className="flex items-start justify-between mb-4">
                                                <h3 className="font-serif text-xl tracking-tight line-clamp-2">
                                                    {set.title}
                                                </h3>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => setDeleteId(set.id)}
                                                    data-testid={`delete-set-${set.id}`}
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity -mt-1 -mr-2 text-muted-foreground hover:text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                                </Button>
                                            </div>

                                            <div className="flex items-center gap-4 text-sm text-muted-foreground mb-6">
                                                <span className="font-mono text-xs">
                                                    {set.card_count} cards
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" strokeWidth={1.5} />
                                                    {formatDate(set.updated_at)}
                                                </span>
                                            </div>

                                            <div className="mt-auto flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => navigate(`/edit/${set.id}`)}
                                                    data-testid={`edit-set-${set.id}`}
                                                    className="rounded-none flex-1"
                                                >
                                                    <Edit2 className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                                    Edit
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => navigate(`/study/${set.id}`)}
                                                    data-testid={`study-set-${set.id}`}
                                                    className="rounded-none flex-1"
                                                    disabled={set.card_count < 2}
                                                >
                                                    <Play className="w-3 h-3 mr-1" strokeWidth={1.5} />
                                                    Study
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
            </main>

            {/* Delete Confirmation */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif">Delete this set?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This action cannot be undone. All flashcards in this set will be permanently deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            data-testid="confirm-delete-button"
                            className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
